import {inject, injectable} from 'inversify';

import type {OfferApiModel} from '../../../../api/offer';
import type {IApiDataMapper} from '../../../../core/ports/types';

import {PaymentPlanMapperJrpc} from '../../common/PaymentPlan/mappers/PaymentPlan.mapper.jrpc';
import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {FileMapperJrpc} from '../../File/mappers/File.mapper.jrpc';
import {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {OfferFactory} from '../Offer.factory';
import {OfferModel} from '../Offer.model';
import {OfferExpandJrpc} from './Offer.expand.jrpc';

@injectable()
export class OfferMapperJrpc
  implements
    IApiDataMapper<OfferMapperJrpc.ApiData, OfferModel.Props, OfferModel>
{
  constructor(
    @inject(OfferFactory)
    private readonly factory: OfferFactory,
    @inject(OfferExpandJrpc)
    public readonly expand: OfferExpandJrpc,
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(FileMapperJrpc)
    private readonly fileMapper: FileMapperJrpc,
    @inject(SubtenantMapperJrpc)
    private readonly subtenantMapper: SubtenantMapperJrpc,
    @inject(PaymentPlanMapperJrpc)
    private readonly paymentPlanMapper: PaymentPlanMapperJrpc,
  ) {}

  mapDataToModel(apiData: OfferMapperJrpc.ExpandedApiData): OfferModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: OfferMapperJrpc.ExpandedApiData): OfferModel.Props {
    const subtenants = this.subtenants(apiData);

    return {
      avatar: this.avatar(apiData),
      client: this.userMapper.mapDataToModelOrEmpty(apiData.client),
      clientId: apiData.client_id,
      clientName: apiData.client_name,
      clientStatus: this.clientStatus(apiData),
      coachIds: apiData.coach_ids,
      concierge: this.concierge(apiData),
      continuation: this.continuation(apiData),
      created: this.dateTimeMapper.mapToModel(apiData.created),
      cycleDuration: apiData.duration,
      description: apiData.description,
      employer: this.userMapper.mapDataToModelOrEmpty(apiData.employer),
      id: apiData._id,
      isAutoRestarting: apiData.is_autorestarting || false,
      numberOfCycles: apiData.num_cycles ?? 1,
      parentOfferId: apiData.parent_offer_id,
      paymentPlan: this.paymentPlan(apiData),
      sessions: this.sessions(apiData),
      subtenants,
      title: apiData.title,
      type: this.type(apiData),
      updated: this.dateTimeMapper.mapToModel(apiData.updated),
      userQuestionnaireId: apiData.user_questionnaire_id,
      visibility: this.visibility(apiData),
    };
  }

  private avatar(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['avatar'] {
    return apiData.avatar
      ? this.fileMapper.mapDataToModel(apiData.avatar)
      : null;
  }

  private clientStatus(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['clientStatus'] {
    switch (apiData.client_status) {
      case 'archived':
        return OfferModel.ClientStatus.ARCHIVED;
      case 'canceled':
        return OfferModel.ClientStatus.CANCELED;
      case 'coach_assigned':
        return OfferModel.ClientStatus.COACH_ASSIGNED;
      case 'coaches_offered':
        return OfferModel.ClientStatus.COACHES_OFFERED;
      case 'concierge_assigned':
        return OfferModel.ClientStatus.CONCIERGE_ASSIGNED;
      case 'ongoing':
        return OfferModel.ClientStatus.ONGOING;
      case 'questionnaire_answered':
        return OfferModel.ClientStatus.QUESTIONNAIRE_ANSWERED;
      case 'questionnaire_sent':
        return OfferModel.ClientStatus.QUESTIONNAIRE_SENT;
      case 'waiting_matching':
        return OfferModel.ClientStatus.WAITING_FOR_MATCHING;
      default:
        return OfferModel.ClientStatus.REGISTERED;
    }
  }

  private concierge(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['concierge'] {
    return apiData.concierge
      ? this.userMapper.mapDataToModelOrEmpty(apiData.concierge)
      : null;
  }

  private continuation(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['continuation'] {
    switch (apiData.continuation) {
      case 'initial':
        return OfferModel.Continuation.INITIAL;
      case 'renewal':
        return OfferModel.Continuation.RENEWAL;
      default:
        return null;
    }
  }

  private paymentPlan(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['paymentPlan'] {
    return apiData.payment_plan
      ? this.paymentPlanMapper.mapDataToModel(apiData.payment_plan)
      : null;
  }

  private sessions(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['sessions'] {
    const base = apiData.client_conferences.count;
    const extra = apiData.client_conferences.extra_sessions_limit ?? 0;
    const total = base + extra;

    return {
      count: {
        base,
        extra,
        total,
      },
      duration: apiData.client_conferences.duration,
      intersessionBreak: apiData.client_conferences.intersession_break || 0,
      minPause: apiData.client_conferences.min_pause,
    };
  }

  private subtenants(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['subtenants'] {
    return (apiData.subtenants ?? []).map(subtenant =>
      subtenant ? this.subtenantMapper.mapExpandedToModel(subtenant) : null,
    );
  }

  private type(apiData: OfferMapperJrpc.ExpandedApiData): OfferModel.Type {
    switch (apiData.type) {
      case 'corporate':
        return OfferModel.Type.CORPORATE;
      case 'personal':
        return OfferModel.Type.PERSONAL;
    }
  }

  private visibility(
    apiData: OfferMapperJrpc.ExpandedApiData,
  ): OfferModel.Props['visibility'] {
    switch (apiData.visibility) {
      case 'archived':
        return OfferModel.Visibility.ARCHIVED;
      case 'public':
        return OfferModel.Visibility.PUBLIC;
      default:
        return OfferModel.Visibility.PRIVATE;
    }
  }
}

export namespace OfferMapperJrpc {
  export type ApiData = OfferApiModel;

  export type ExpandedApiData = ApiData & OfferExpandJrpc.ExpandedFields;
}
